/**
 * THE SESSION-END SIGNAL: the Apollo link raises it, ONE owner acts on it.
 *
 * The link never signs anyone out. It cannot know what the page is doing: on
 * Book a booking may be mid-send or its outcome unknown, and signing out then
 * would orphan the only handle on it. So the link raises this, and the owner
 * (Book's IdentityProvider, App's SessionEndOwner) decides when.
 *
 * Raised ONLY for a credentialed request: one that carried the ID token of a
 * non-anonymous Firebase user (see `SessionCredential`). An anonymous guest's
 * request never raises it, whatever the body says.
 *
 * Holds no UID beyond the moment it is delivered, and logs nothing.
 */

export type SessionEndReason =
  /** Backend said SESSION_REVOKED. Sign out and route to sign-in. */
  | "session-revoked"
  /** The Firebase SDK refused to refresh the identity (the legacy rule). Sign out. */
  | "identity-invalid";

export interface SessionEndSignal {
  readonly reason: SessionEndReason;
  /**
   * The UID whose request was refused. Always known: only a credentialed
   * request raises the signal, and a credential always names its UID. An
   * owner whose current user is a different UID ignores the signal: the
   * person already changed.
   */
  readonly uid: string;
}

/**
 * WHO a request was sent as, recorded on the operation's context by each
 * app's auth link and read back by the session link.
 */
export type SessionCredential =
  | { readonly kind: "credentialed"; readonly uid: string }
  | { readonly kind: "anonymous" }
  | { readonly kind: "none" };

export const SESSION_CREDENTIAL_CONTEXT_KEY = "luraSessionCredential";
export const NO_CREDENTIAL: SessionCredential = Object.freeze({ kind: "none" });
export const ANONYMOUS_CREDENTIAL: SessionCredential = Object.freeze({ kind: "anonymous" });

/** Read the credential an auth link recorded; anything else is "none". */
export function credentialFromContext(context: Record<string, unknown>): SessionCredential {
  const value = context[SESSION_CREDENTIAL_CONTEXT_KEY] as SessionCredential | undefined;
  if (value && value.kind === "credentialed" && typeof value.uid === "string" && value.uid) return value;
  if (value && value.kind === "anonymous") return value;
  return NO_CREDENTIAL;
}

type Listener = (signal: SessionEndSignal) => void;
const listeners = new Set<Listener>();

/**
 * Raise the signal. With no owner subscribed nothing happens: no surface
 * sends a credentialed request without its owner mounted above it.
 */
export function raiseSessionEnd(signal: SessionEndSignal): void {
  for (const listener of [...listeners]) {
    try {
      listener(signal);
    } catch {
      // One owner's failure must not stop the error reaching the caller.
    }
  }
}

/** Subscribe the owner. Returns the unsubscribe. */
export function onSessionEnd(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only. */
export function resetSessionEndForTests(): void {
  listeners.clear();
}
